import { Component, HostListener, ElementRef, OnInit, AfterViewChecked, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { State } from '@completion/reducers';
import { getHeatTraceConfigured, getRole } from '@completion/selectors';
import { UserAccessService } from '../../core/services/user-access.service';
import { KEYS } from '../../shared/constants/keyboard-keys';
import { ProjectAccessRoles } from '@completion/models';

export interface AppItem {
  label: string;
  icon: string;
  route?: string;
  url?: string;
  disabled?: boolean;
  /** Visible only when the user has the AAD ADMIN role. */
  adminOnly?: boolean;
  /** Visible for AAD ADMIN **or** project ADMIN. */
  projectSetup?: boolean;
  /** Visible only when the user has the AAD USER_RW role. */
  requiresUserRW?: boolean;
  /** Hidden for users with the VENDOR project role. */
  requiresNonVendor?: boolean;
  /** Visible only if the active project has Heat Trace configured. */
  requiresHeatTraceConfigured?: boolean;
}

@Component({
  selector: 'app-launcher',
  templateUrl: './app-launcher.component.html',
  styleUrls: ['./app-launcher.component.scss'],
  standalone: false,
})
export class AppLauncherComponent implements OnInit, AfterViewChecked, OnDestroy {
  isOpen = false;
  isAdmin = false;
  isUserRW = false;
  isProjectAdmin = false;
  isVendor = false;
  heatTraceConfigured: boolean | null = null;
  private focusAfterOpen = false;
  private readonly destroy$ = new Subject<void>();

  private readonly allModules: AppItem[] = [
    { label: 'Reports', icon: 'assessment', route: '/reports', requiresUserRW: true, requiresNonVendor: true },
    { label: 'Supplier', icon: 'business', route: '/supplier', requiresUserRW: true, requiresNonVendor: true },
    { label: 'Bulk', icon: 'dynamic_feed', route: '/batch', requiresUserRW: true, requiresNonVendor: true },
    { label: 'Project Overview', icon: 'account_tree', route: '/project-overview', requiresUserRW: true, requiresNonVendor: true },
    {
      label: 'Heat Tracing',
      icon: 'thermostat',
      route: '/heat-trace',
      requiresUserRW: true,
      requiresNonVendor: true,
      requiresHeatTraceConfigured: true,
    },
    {
      label: 'Punch Round',
      icon: 'playlist_add_check',
      route: '/punch-round',
      disabled: true,
      requiresUserRW: true,
      requiresNonVendor: true,
    },
  ];

  private readonly allAdminModules: AppItem[] = [
    { label: 'Project Setup', icon: 'settings', route: '/project-setup', projectSetup: true },
    { label: 'Admin', icon: 'admin_panel_settings', route: '/admin', adminOnly: true },
  ];

  // TODO: Move sibling apps configuration to the database
  // private readonly allSiblingApps: AppItem[] = [
  //   { label: 'DWO – Digital Work Order', icon: 'assignment', url: 'https://dwo.akersolutions.com' },
  // ];

  visibleModules: AppItem[] = [];
  visibleAdminModules: AppItem[] = [];
  visibleSiblingApps: AppItem[] = [];

  constructor(
    private readonly router: Router,
    private readonly el: ElementRef,
    private readonly store: Store<State>,
    private readonly userAccess: UserAccessService,
  ) {}

  ngOnInit(): void {
    this.userAccess.isGlobalAdmin$.pipe(takeUntil(this.destroy$)).subscribe((isAdmin) => {
      this.isAdmin = isAdmin;
      this.rebuildVisibleModules();
    });

    this.userAccess.hasUserRW$.pipe(takeUntil(this.destroy$)).subscribe((isUserRW) => {
      this.isUserRW = isUserRW;
      this.rebuildVisibleModules();
    });

    this.userAccess.isProjectAdmin$.pipe(takeUntil(this.destroy$)).subscribe((isProjectAdmin) => {
      this.isProjectAdmin = isProjectAdmin;
      this.rebuildVisibleModules();
    });

    this.store
      .select(getRole)
      .pipe(takeUntil(this.destroy$))
      .subscribe((role) => {
        this.isVendor = role === ProjectAccessRoles.VENDOR;
        this.rebuildVisibleModules();
      });

    this.store
      .select(getHeatTraceConfigured)
      .pipe(takeUntil(this.destroy$))
      .subscribe((heatTraceConfigured) => {
        this.heatTraceConfigured = heatTraceConfigured;
        this.rebuildVisibleModules();
      });

    this.rebuildVisibleModules();
  }

  private rebuildVisibleModules(): void {
    const filterFn = (m: AppItem) => {
      if (m.adminOnly && !this.isAdmin) return false;
      if (m.projectSetup && !this.isAdmin && !this.isProjectAdmin) return false;
      if (m.requiresUserRW && !this.isUserRW) return false;
      if (m.requiresNonVendor && this.isVendor) return false;
      return true;
    };

    this.visibleModules = this.allModules.filter(filterFn).map((m) => {
      if (m.requiresHeatTraceConfigured && this.heatTraceConfigured !== true) {
        return { ...m, disabled: true };
      }

      return m;
    });
    this.visibleAdminModules = this.allAdminModules.filter(filterFn);
    // this.visibleSiblingApps = this.allSiblingApps.filter((m) => !m.adminOnly || this.isAdmin);
  }

  toggle(): void {
    this.isOpen = !this.isOpen;
  }

  close(): void {
    this.isOpen = false;
  }

  navigate(item: AppItem): void {
    if (item.disabled) return;
    if (item.url) {
      window.open(item.url, '_blank');
    } else if (item.route) {
      this.router.navigate([item.route]);
    }
    this.close();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.el.nativeElement.contains(event.target)) {
      this.isOpen = false;
    }
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    const key = event.key.toLowerCase();

    // Open launcher with 'a' or 'o' when no input is focused
    if (key === KEYS.A || key === KEYS.O) {
      const tag = (event.target as HTMLElement)?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || (event.target as HTMLElement)?.isContentEditable) return;
      event.preventDefault();
      this.isOpen = true;
      this.focusAfterOpen = true;
      return;
    }

    // Close on Escape
    if (key === KEYS.ESCAPE && this.isOpen) {
      this.isOpen = false;
      return;
    }

    // Arrow / Tab navigation within the panel
    if (!this.isOpen) return;
    if (key !== KEYS.ARROW_UP && key !== KEYS.ARROW_DOWN && key !== KEYS.ARROW_LEFT && key !== KEYS.ARROW_RIGHT && key !== KEYS.TAB) return;

    const items = this.getLauncherItems();
    if (!items.length) return;

    const currentIndex = items.indexOf(document.activeElement as HTMLElement);
    const cols = 3; // items per row in the grid
    event.preventDefault();

    let next: number;
    if (key === KEYS.ARROW_RIGHT || (key === KEYS.TAB && !event.shiftKey)) {
      next = currentIndex < 0 ? 0 : (currentIndex + 1) % items.length;
    } else if (key === KEYS.ARROW_LEFT || (key === KEYS.TAB && event.shiftKey)) {
      next = currentIndex <= 0 ? items.length - 1 : currentIndex - 1;
    } else if (key === KEYS.ARROW_DOWN) {
      next = currentIndex < 0 ? 0 : currentIndex + cols < items.length ? currentIndex + cols : (currentIndex + 1) % items.length;
    } else {
      // arrowup
      next =
        currentIndex < 0
          ? items.length - 1
          : currentIndex - cols >= 0
            ? currentIndex - cols
            : (currentIndex - 1 + items.length) % items.length;
    }
    items[next]?.focus();
  }

  ngAfterViewChecked(): void {
    if (this.focusAfterOpen && this.isOpen) {
      const items = this.getLauncherItems();
      if (items.length) {
        items[0].focus();
        this.focusAfterOpen = false;
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getLauncherItems(): HTMLElement[] {
    const panel = this.el.nativeElement.querySelector('.launcher-panel');
    return panel ? Array.from(panel.querySelectorAll('.launcher-item')) : [];
  }
}
